import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import config from "../../config";
import { FALLBACK_LANG } from "../../languages";
import translations from "../../translations/blog";
import headerTranslations from "../header/header-translations";
import styles from "./ArticlesList.module.css";
import { ArrowIcon, PhoneIcon, RouteIcon } from "./BlogIcons";
import {
  blogPath, quotePath, pickTranslation, formatDate, displayDate, isRevised, formatDuration,
  cloudinary, cloudinarySrcSet, frenchSpacing,
} from "./articleUtils";

const CARD_IMAGE = "c_fill,g_auto,ar_3:2";

const CardMeta = ({ article, lang, withOrigin }) => {
  const t = translations[lang];
  return (
    <div className={styles.row}>
      {article.trip && article.trip.minutes > 0 && (
        <span className={styles.route}>
          <RouteIcon />
          {formatDuration(article.trip.minutes, lang)}{withOrigin && " " + t.fromLyon}
        </span>
      )}
      <span className={styles.meta}>
        <time dateTime={displayDate(article)}>{isRevised(article) && t.updated + " "}{formatDate(displayDate(article), lang)}</time>
        {article.readingMinutes > 0 && (
          <>
            <i className={styles.dot} />
            {article.readingMinutes} {t.readTime}
          </>
        )}
      </span>
    </div>
  );
};

const ArticleCard = ({ article, lang }) => {
  const category = translations[lang].categories[article.category];
  return (
    <Link className={styles.card} to={blogPath(article.lang) + article.slug}>
      <div className={styles.cardImg}>
        <img
          src={cloudinary(article.imageLink, CARD_IMAGE + ",w_800")}
          srcSet={cloudinarySrcSet(article.imageLink, CARD_IMAGE, [400, 800, 1200])}
          sizes="(max-width: 680px) 100vw, (max-width: 1000px) 50vw, 380px"
          alt={article.thumbAlt || article.imageAlt || ""}
          loading="lazy"
        />
      </div>
      {category && <span className={styles.eyebrow}>{category}</span>}
      <h3 className={styles.cardTitle}>{frenchSpacing(article.title, lang)}</h3>
      <CardMeta article={article} lang={lang} />
    </Link>
  );
};

const FeaturedCard = ({ article, lang }) => {
  const t = translations[lang];
  const category = t.categories[article.category];
  return (
    <Link className={styles.featured} to={blogPath(article.lang) + article.slug}>
      <div className={styles.featuredImg}>
        <img
          src={cloudinary(article.imageLink, CARD_IMAGE + ",w_1200")}
          srcSet={cloudinarySrcSet(article.imageLink, CARD_IMAGE, [800, 1200, 1600])}
          sizes="(max-width: 1000px) 100vw, 660px"
          alt={article.imageAlt || ""}
        />
      </div>
      <div className={styles.featuredBody}>
        <span className={styles.eyebrow}>{[category, t.featured].filter(Boolean).join(" · ")}</span>
        <h2 className={styles.featuredTitle}>{frenchSpacing(article.title, lang)}</h2>
        <p className={styles.featuredLead}>{frenchSpacing(article.excerpt || article.metaDescription, lang)}</p>
        <CardMeta article={article} lang={lang} withOrigin />
        <span className={styles.readMore}>{t.readMore} <ArrowIcon /></span>
      </div>
    </Link>
  );
};

const QuoteCard = ({ lang }) => {
  const t = translations[lang];
  return (
    <aside className={styles.ctaCard}>
      <div>
        <span className={styles.eyebrow}>{t.ctaEyebrow}</span>
        <h3>{t.ctaTitle}</h3>
        <p>{t.ctaText}</p>
      </div>
      <div className={styles.ctaActions}>
        <Link className={styles.btnPrimary} to={quotePath(lang)}>{t.quote}</Link>
        <a className={styles.btnGhost} href="tel:+33660265336"><PhoneIcon />{headerTranslations[lang].phone}</a>
      </div>
    </aside>
  );
};

// variant "blog": featured article, category filters and a quote card in the grid (Blog page).
// Default: plain grid (homepage, related articles).
const ArticlesList = (props) => {
  const { lang, limit, variant, exclude, max } = props;
  const t = translations[lang];
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState(null);

  useEffect(() => {
    axios
      .get(config["api-endpoint"] + "/articles?lang=" + lang + "&limit=" + limit, {
        headers: {
          "x-api-key": config["x-api-key"],
        },
      })
      .then((res) => {
        // A language without translated articles yet shows the English ones, linked to their English pages.
        const own = res.data.map((article) => pickTranslation(article, lang)).filter(Boolean);
        setArticles(own.length || lang === FALLBACK_LANG
          ? own
          : res.data.map((article) => pickTranslation(article, FALLBACK_LANG)).filter(Boolean));
      })
      .catch((err) => {
        console.log(err);
      });
  }, [lang, limit]);

  let list = articles.filter((article) => article.slug !== exclude);
  const fallback = list.length > 0 && list[0].lang !== lang;
  if (max) list = list.slice(0, Number(max));

  if (variant !== "blog") {
    return (
      <div className={styles.grid}>
        {list.map((article) => <ArticleCard key={article._id} article={article} lang={lang} />)}
      </div>
    );
  }

  const categories = Object.keys(t.categories).filter((key) => list.some((a) => a.category === key));
  const featured = category ? null : list.find((a) => a.featured) || list[0];
  const rest = list.filter((a) => a !== featured && (!category || a.category === category));
  const cards = rest.map((article) => <ArticleCard key={article._id} article={article} lang={lang} />);
  if (cards.length) cards.splice(Math.min(5, cards.length), 0, <QuoteCard key="quote" lang={lang} />);

  return (
    <>
      {fallback && <p className={styles.fallback}>{t.fallback}</p>}
      {categories.length > 1 && (
        <div className={styles.filters} role="group">
          {[null, ...categories].map((key) => (
            <button
              key={key || "all"}
              type="button"
              className={styles.chip}
              aria-pressed={category === key}
              onClick={() => setCategory(key)}
            >
              {key ? t.categories[key] : t.all}
              <small>{key ? list.filter((a) => a.category === key).length : list.length}</small>
            </button>
          ))}
        </div>
      )}
      {featured && <FeaturedCard article={featured} lang={lang} />}
      {cards.length > 0 && (
        <>
          {featured && <h2 className={styles.sectionTitle}>{t.latest}</h2>}
          <div className={styles.grid}>{cards}</div>
        </>
      )}
    </>
  );
};

export default ArticlesList;
